// @flow
import { BetService } from "openbet-player-api-sdk";
import { normalize } from "normalizr";
import { PricingError, errorForKey } from "../../errors/pricingError";
import { pricingApiSchema, transformKeys } from "./pricingApiSchema";
import type { BettingApi, ApiConfig, PriceFormat, BetPrice } from "bb-api-sdk";
import { BuilderBetslipError } from "bb-betslip";

/**
 * Service class used for building request parameters to the Player API
 *
 * @ignore
 */
class BuilderBetService extends BetService {
  /**
   * Maps a list of outcome Ids to a request object expected by Player API
   *
   * @ignore
   * @param outcomeIds
   * @returns {{legs: {outcomes: Array<{outcomeRef: T}>, options: {combinationRef: string}, documentId: string}[]}}
   * @private
   */
  static _sdkOutcomesToBuilderLegs(outcomes: [any]) {
    
    return {
      legs: [
        {
          options: {
            combinationRef: "BUILDER_BET",
            handicaps: outcomes.map((outcome) => {
              return {
                outcomeRef: outcome.selectionId,
              };
            }),
          },
          documentId: "BUILDER_BET_1",
          outcomes: outcomes.map((outcome) => {
            return {
              outcomeRef: outcome.selectionId,
            };
          }),
        },
      ],
    };
  }

  /**
   * Builds the request
   *
   * @ignore
   * @param outcomes
   * @returns {*}
   */
  buildBLDRSlip(outcomes: [any]) {
    const apiLegs = BuilderBetService._sdkOutcomesToBuilderLegs(outcomes);    
    const service = "bet";
    return this.sendHTTP({
      service,
      headers: {
        // 'Content-Type': 'application/json;charset=UTF-8',
        // 'X-OB-Channel': 'I',
        Accept: "application/json",
      },
      resource: "/slips/local/build",
      method: "POST",
      body: apiLegs,
      authzn: {
        anonymous: true,
      },
      deadline: this.deadlines.buildSlip,
    });
  }
}

/**
 * An implementation of the BettingAPI, which uses OB Player API as the communication layer
 *
 * @ignore
 */
export default class SGPricingService implements BettingApi {
  /**
   * Returns a formatted display price
   *
   * @ignore
   * @param price - The price object
   * @param format - Required price format
   * @returns {string} - The display price
   */
  displayPrice(price: BetPrice, format: PriceFormat): string {
    if (price == null) return "?";
    switch (format) {
      case "decimal":
        return `${price.decimal}`;
      case "decimal-comma":
        return `${price.decimal.replace(".", ",")}`;
      case "fractional":
        return `${price.numerator}/${price.denominator}`;
      case "american":
        if (price.decimal >= 2.0)
          return `+${Math.round((price.decimal - 1.0) * 100)}`;
        else return `-${Math.round(100 / (price.decimal - 1.0))}`;
      default:
        return "?";
    }
  }

  /**
   * Fetches a new price for a list of outcome Ids
   *
   * @param config
   * @param outcomeIds
   * @returns {Promise<Promise<*>|Promise<*>>}
   */
  fetchPrice = async (config: ApiConfig, outcomes: any) => {
    if (config == null || config.endpoint == null) {
      throw Error("Must configure bb-content-api before loading data");
    }

    const service = new BuilderBetService({
      endpoints: {
        bet: config.endpoint,
      },
    });

    return service
      .buildBLDRSlip(outcomes)
      .then(async (response) => {
        if (response.ok) return response.json();
        if (response.status >= 400 && response.status < 500) {
          const errResponse = await response.json();
          if (errResponse !== null) {
            errResponse.data = transformKeys(
              normalize(errResponse.data, pricingApiSchema)
            );
            if (
              errResponse.errors[0].id === "BET_PRICE_NOT_AVAILABLE" &&
              Object.keys(errResponse.data.outcomes).length < 2
            ) {
              return { data: errResponse.data };
            }
            const errors = errResponse.errors;
            if (errors && errors.length > 0) {
              const error = errorForKey(errors[0].id);
              throw new BuilderBetslipError(
                error.title,
                error.type,
                error.message,
                errResponse
              );
            }
          }
        }
        throw new BuilderBetslipError(
          PricingError.GENERIC.title,
          PricingError.GENERIC.type,
          PricingError.GENERIC.message
        );
      })
      .then((json) => {
        if (json.data.status === "BUILD_ERROR") {
          throw new BuilderBetslipError(
            PricingError.GENERIC.title,
            PricingError.GENERIC.type,
            PricingError.GENERIC.message
          );
        }
        return { data: transformKeys(normalize(json.data, pricingApiSchema)) };
      });
  };
}



// WEBPACK FOOTER //
// ./src/apis/pricing/index.js